import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import RefreshIcon from '@mui/icons-material/Refresh';
import RuleIcon from '@mui/icons-material/Rule';
import SearchIcon from '@mui/icons-material/Search';
import SmartImage from '../components/SmartImage';
import {
  applyProductEnrichment,
  approveEnrichmentReview,
  EnrichmentDecision,
  EnrichmentReview as EnrichmentReviewItem,
  EnrichmentReviewDetail,
  fetchEnrichmentReviewDetail,
  fetchEnrichmentReviews,
  rejectEnrichmentReview,
  runBestsellerEnrichment,
} from '../services/enrichmentService';

const severityColor: Record<string, 'error' | 'warning' | 'info' | 'default'> =
  {
    high: 'error',
    medium: 'warning',
    low: 'info',
  };

const statusColor: Record<
  string,
  'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  open: 'warning',
  approved: 'info',
  rejected: 'error',
  resolved: 'success',
  staged: 'info',
  applied: 'success',
  needs_review: 'warning',
  failed: 'error',
  completed: 'success',
  running: 'info',
  queued: 'default',
};

function formatPercent(value: number | string | null | undefined) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '0%';
  return `${Math.round(parsed * 100)}%`;
}

function decisionIcon(type: string) {
  switch (type) {
    case 'category':
      return <RuleIcon fontSize="small" />;
    case 'image_order':
    case 'background':
      return <ImageSearchIcon fontSize="small" />;
    default:
      return <PlaylistAddCheckIcon fontSize="small" />;
  }
}

const getImagesFromDecision = (decision: EnrichmentDecision | undefined) => {
  const output = decision?.output as { images?: EnrichmentImageItem[] } | null;
  const images = output?.images;
  return Array.isArray(images) ? images : [];
};

interface EnrichmentImageItem {
  url: string;
  type?: string;
  role?: string;
  confidence?: number;
  autoRemoveBackground?: boolean;
}

const EnrichmentReview: React.FC = () => {
  const [reviews, setReviews] = useState<EnrichmentReviewItem[]>([]);
  const [counts, setCounts] = useState({
    open: 0,
    approved: 0,
    rejected: 0,
    resolved: 0,
    highOpen: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
  });
  const [status, setStatus] = useState('open');
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [manualProductNumber, setManualProductNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [detail, setDetail] = useState<EnrichmentReviewDetail | null>(null);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  const selectedImageDecision = useMemo(
    () =>
      detail?.decisions.find(
        (decision) => decision.decisionType === 'image_order',
      ),
    [detail],
  );
  const selectedBackgroundDecision = useMemo(
    () =>
      detail?.decisions.find(
        (decision) => decision.decisionType === 'background',
      ),
    [detail],
  );

  const loadReviews = async (nextPage = pagination.page) => {
    setLoading(true);
    try {
      const response = await fetchEnrichmentReviews({
        page: nextPage,
        limit: pagination.limit,
        status,
        reason: reason || undefined,
        search: search || undefined,
      });
      setReviews(response.reviews);
      setCounts(response.counts);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
      });
    } catch (error) {
      console.error('Failed to load enrichment reviews:', error);
      setSnackbar({
        message: 'Kunde inte hämta enrichment-granskningar',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (reviewId: number) => {
    setSelectedReviewId(reviewId);
    setDetailLoading(true);
    try {
      const response = await fetchEnrichmentReviewDetail(reviewId);
      setDetail(response);
    } catch (error) {
      console.error('Failed to load enrichment review detail:', error);
      setSnackbar({
        message: 'Kunde inte hämta detaljer för granskningen',
        severity: 'error',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadReviews(1);
  }, [status, reason]);

  const handleSearch = () => {
    loadReviews(1);
  };

  const handleApprove = async () => {
    if (!detail) return;
    try {
      await approveEnrichmentReview(detail.review.reviewId);
      setSnackbar({ message: 'Granskningen godkändes', severity: 'success' });
      await loadDetail(detail.review.reviewId);
      await loadReviews();
    } catch (error) {
      setSnackbar({
        message: 'Kunde inte godkänna granskningen',
        severity: 'error',
      });
    }
  };

  const handleReject = async () => {
    if (!detail) return;
    try {
      await rejectEnrichmentReview(detail.review.reviewId);
      setSnackbar({ message: 'Granskningen avvisades', severity: 'success' });
      await loadDetail(detail.review.reviewId);
      await loadReviews();
    } catch (error) {
      setSnackbar({
        message: 'Kunde inte avvisa granskningen',
        severity: 'error',
      });
    }
  };

  const handleApply = async () => {
    if (!detail) return;
    try {
      const result = await applyProductEnrichment(detail.review.productId);
      setSnackbar({
        message: `Applicerat: ${result.applied.join(', ') || 'inget'}`,
        severity: 'success',
      });
      await loadDetail(detail.review.reviewId);
      await loadReviews();
    } catch (error) {
      setSnackbar({
        message: 'Kunde inte applicera enrichment',
        severity: 'error',
      });
    }
  };

  const handleRunManual = async () => {
    const productNumber = manualProductNumber.trim();
    if (!productNumber) {
      setSnackbar({ message: 'Ange ett artikelnummer', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const result = await runBestsellerEnrichment(productNumber);
      const missing = result.missingProductNumbers || [];
      setSnackbar({
        message:
          missing.length > 0
            ? `Hittade inte artikelnummer: ${missing.join(', ')}`
            : 'Bestseller enrichment kördes',
        severity: missing.length > 0 ? 'error' : 'success',
      });
      if (missing.length === 0) setManualProductNumber('');
      await loadReviews(1);
    } catch (error) {
      setSnackbar({
        message: 'Kunde inte köra Bestseller enrichment',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: 'text.primary' }}
          >
            Bestseller enrichment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Granska automatiska kategori-, bild- och publiceringsbeslut innan de
            appliceras.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label="Artikelnummer"
            value={manualProductNumber}
            onChange={(event) => setManualProductNumber(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleRunManual();
            }}
            sx={{ width: { xs: '100%', sm: 180 } }}
          />
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={handleRunManual}
            disabled={loading}
          >
            Kör manuellt
          </Button>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems="center"
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <MenuItem value="open">Öppna</MenuItem>
              <MenuItem value="approved">Godkända</MenuItem>
              <MenuItem value="rejected">Avvisade</MenuItem>
              <MenuItem value="resolved">Lösta</MenuItem>
              <MenuItem value="all">Alla</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Orsak</InputLabel>
            <Select
              label="Orsak"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            >
              <MenuItem value="">Alla orsaker</MenuItem>
              <MenuItem value="Kategori behöver granskas">Kategori</MenuItem>
              <MenuItem value="Bildordning behöver granskas">
                Bildordning
              </MenuItem>
              <MenuItem value="Transparent bakgrund behöver granskas">
                Transparent bakgrund
              </MenuItem>
              <MenuItem value="Bestseller API misslyckades">API-fel</MenuItem>
              <MenuItem value="Beskrivning behöver granskas">
                Beskrivning
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Sök"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSearch();
            }}
            sx={{ flex: 1, minWidth: 220 }}
          />

          <Tooltip title="Sök">
            <IconButton onClick={handleSearch}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Uppdatera">
            <IconButton onClick={() => loadReviews()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Chip
            label={`Öppna ${counts.open || 0}`}
            color="warning"
            variant="outlined"
          />
          <Chip
            label={`Hög prio ${counts.highOpen || 0}`}
            color="error"
            variant="outlined"
          />
          <Chip
            label={`Godkända ${counts.approved || 0}`}
            color="info"
            variant="outlined"
          />
          <Chip
            label={`Lösta ${counts.resolved || 0}`}
            color="success"
            variant="outlined"
          />
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produkt</TableCell>
              <TableCell>Orsak</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Jobb</TableCell>
              <TableCell align="right">Beslut</TableCell>
              <TableCell>Skapad</TableCell>
              <TableCell align="right">Åtgärd</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    Inga granskningar matchar filtret.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => (
                <TableRow key={review.reviewId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {review.productNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {review.brandName || '-'} · {review.productName || '-'}
                    </Typography>
                    {!review.productExists && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ display: 'block', fontWeight: 700 }}
                      >
                        Saknar matchande produktpost
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={review.severity}
                        color={severityColor[review.severity] || 'default'}
                      />
                      <Typography variant="body2">{review.reason}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={review.status}
                      color={statusColor[review.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={review.jobStatus || '-'}
                      color={statusColor[review.jobStatus || ''] || 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{review.decisionCount}</TableCell>
                  <TableCell>
                    {new Date(review.createdAt).toLocaleString('sv-SE')}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => loadDetail(review.reviewId)}
                    >
                      Granska
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={Math.max(0, pagination.page - 1)}
          onPageChange={(_, nextPage) => loadReviews(nextPage + 1)}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[25]}
        />
      </TableContainer>

      <Drawer
        anchor="right"
        open={selectedReviewId !== null}
        onClose={() => {
          setSelectedReviewId(null);
          setDetail(null);
        }}
        PaperProps={{
          sx: { width: { xs: '100%', md: 760 }, p: 3 },
        }}
      >
        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : detail ? (
          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {detail.review.productNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detail.review.brandName || '-'} ·{' '}
                  {detail.review.productName || '-'}
                </Typography>
                {!detail.review.productExists && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    Den här review-raden pekar på ett productId som inte finns i
                    Products. Avvisa raden eller kör om enrichment med ett
                    giltigt productId.
                  </Alert>
                )}
              </Box>
              <IconButton
                onClick={() => {
                  setSelectedReviewId(null);
                  setDetail(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            {detail.review.lastError && (
              <Alert icon={<ErrorOutlineIcon />} severity="error">
                {detail.review.lastError}
              </Alert>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
              <Chip label={detail.review.reason} color="warning" />
              <Chip
                label={`Jobb: ${detail.review.jobStatus || '-'}`}
                variant="outlined"
              />
              <Chip
                label={`Publicering: ${detail.review.publishDate || 'saknas'}`}
                variant="outlined"
              />
            </Stack>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Beslut
              </Typography>
              <Stack spacing={1.25}>
                {detail.decisions.map((decision) => (
                  <Paper
                    key={decision.decisionId}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        {decisionIcon(decision.decisionType)}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {decision.decisionType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Nivå {decision.level} · confidence{' '}
                            {formatPercent(decision.confidence)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        size="small"
                        label={decision.status}
                        color={statusColor[decision.status] || 'default'}
                      />
                    </Stack>
                    {decision.rationale && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {decision.rationale}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Föreslagen bildordning
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ overflowX: 'auto', pb: 1 }}
              >
                {getImagesFromDecision(selectedImageDecision).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Ingen Bestseller-bild hittades i beslutet.
                  </Typography>
                ) : (
                  getImagesFromDecision(selectedImageDecision).map(
                    (image, index) => (
                      <Box
                        key={`${image.url}-${index}`}
                        sx={{
                          width: 136,
                          flex: '0 0 auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ aspectRatio: '3 / 4', bgcolor: '#F8FAFC' }}>
                          <img
                            src={image.url}
                            alt={image.role || `Bild ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', fontWeight: 700 }}
                          >
                            {index + 1}. {image.role || 'other'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {image.type || '-'} ·{' '}
                            {formatPercent(image.confidence)}
                          </Typography>
                        </Box>
                      </Box>
                    ),
                  )
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Bakgrundsbeslut
              </Typography>
              <Stack spacing={1}>
                {getImagesFromDecision(selectedBackgroundDecision).map(
                  (image, index) => (
                    <Stack
                      key={`${image.url}-${index}`}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>
                        {image.type || image.role || image.url}
                      </Typography>
                      <Chip
                        size="small"
                        color={
                          image.autoRemoveBackground ? 'success' : 'warning'
                        }
                        label={image.autoRemoveBackground ? 'Auto' : 'Granska'}
                      />
                    </Stack>
                  ),
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Nuvarande media
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ overflowX: 'auto', pb: 1 }}
              >
                {detail.media.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Ingen media sparad på produkten.
                  </Typography>
                ) : (
                  detail.media.map((media) => (
                    <Box
                      key={`${media.attribute1Number}-${media.imageUrl}`}
                      sx={{ width: 96, flex: '0 0 auto' }}
                    >
                      <SmartImage
                        imageName={media.imageUrl}
                        alt={media.attribute1Number}
                        style={{
                          width: '100%',
                          aspectRatio: '3 / 4',
                          objectFit: 'contain',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {media.attribute1Number}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Lager per färg
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                {detail.stock.map((item) => (
                  <Chip
                    key={item.attribute1Number}
                    label={`${item.attribute1Code} (${item.attribute1Number}): ${item.availableBalance}`}
                    color={item.availableBalance > 0 ? 'success' : 'default'}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleApprove}
                disabled={detail.review.status !== 'open'}
              >
                Godkänn
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={handleReject}
                disabled={detail.review.status !== 'open'}
              >
                Avvisa
              </Button>
              <Button
                variant="outlined"
                startIcon={<PublishedWithChangesIcon />}
                onClick={handleApply}
              >
                Applicera staged
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography color="text.secondary">Välj en granskning.</Typography>
        )}
      </Drawer>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default EnrichmentReview;
